import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface PodcastMediaSource {
  id: number | undefined;
  url: string | undefined;
}

interface PodcastPlayerState {
  audioRef: React.RefObject<HTMLAudioElement>;
  currentPodcastId: number | null;
  isPlaying: boolean;
  currentTimeMs: number;
  durationMs: number;
  volume: number; // 0..1
  playbackRate: number; // 0.5..2
  play: (source: PodcastMediaSource, startTimeMs?: number) => Promise<void>;
  pause: () => void;
  stop: () => void;
  togglePlayPause: (source?: PodcastMediaSource) => Promise<void>;
  seek: (timeMs: number) => void;
  setVolume: (v: number) => void;
  setPlaybackRate: (r: number) => void;
}

const PodcastPlayerContext = createContext<PodcastPlayerState | null>(null);

export const usePodcastPlayer = (): PodcastPlayerState => {
  const ctx = useContext(PodcastPlayerContext);
  if (!ctx)
    throw new Error(
      "usePodcastPlayer must be used within PodcastPlayerProvider",
    );
  return ctx;
};

export const PodcastPlayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentPodcastId, setCurrentPodcastId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);

  // Wire audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDurationMs((audio.duration || 0) * 1000);
    };
    const handleTimeUpdate = () => {
      setCurrentTimeMs((audio.currentTime || 0) * 1000);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTimeMs(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const play = useCallback(
    async (source: PodcastMediaSource, startTimeMs?: number) => {
      if (!audioRef.current || !source?.url) return;
      const audio = audioRef.current;
      // If switching source
      if (audio.src !== source.url) {
        audio.src = source.url;
        setCurrentPodcastId(source.id ?? null);
        await audio.load();
      }
      if (typeof startTimeMs === "number") {
        audio.currentTime = Math.max(0, startTimeMs / 1000);
      }
      try {
        await audio.play();
      } catch (_) {
        // ignore autoplay errors
      }
    },
    [],
  );

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTimeMs(0);
  }, []);

  const togglePlayPause = useCallback(
    async (source?: PodcastMediaSource) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) {
        if (source?.url && audio.src !== source.url) {
          await play(source, 0);
        } else {
          try {
            await audio.play();
          } catch (_) {}
        }
      } else {
        audio.pause();
      }
    },
    [play],
  );

  const seek = useCallback((timeMs: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, timeMs / 1000);
    setCurrentTimeMs(audio.currentTime * 1000);
  }, []);

  const setVolume = useCallback((v: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.max(0, Math.min(1, v));
    audio.volume = clamped;
    setVolumeState(clamped);
  }, []);

  const setPlaybackRate = useCallback((r: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.max(0.5, Math.min(2, r));
    audio.playbackRate = clamped;
    setPlaybackRateState(clamped);
  }, []);

  const value: PodcastPlayerState = useMemo(
    () => ({
      audioRef,
      currentPodcastId,
      isPlaying,
      currentTimeMs,
      durationMs,
      volume,
      playbackRate,
      play,
      pause,
      stop,
      togglePlayPause,
      seek,
      setVolume,
      setPlaybackRate,
    }),
    [
      currentPodcastId,
      isPlaying,
      currentTimeMs,
      durationMs,
      volume,
      playbackRate,
      play,
      pause,
      stop,
      togglePlayPause,
      seek,
      setVolume,
      setPlaybackRate,
    ],
  );

  return (
    <PodcastPlayerContext.Provider value={value}>
      {children}
      {/* hidden single audio element */}
      <audio ref={audioRef} className="hidden" preload="metadata" />
    </PodcastPlayerContext.Provider>
  );
};
